/**
 * Full-window viewer for a slice a shared report mentions.
 *
 * The picture a reader gets is the one the doctor drew on. The clear button
 * lifts those drawings for as long as the image stays open — it is a way of
 * looking, not an edit: the state lives in this component, so closing and
 * reopening the slice brings the drawings back. Both frames are mounted at
 * once and swapped by opacity, which keeps the toggle instant and keeps the
 * image from shifting under the reader.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronLeft, ChevronRight, Eye, EyeOff, X, ZoomIn, ZoomOut } from 'lucide-react';

import type { SharedSliceRef } from './sharedReportApi';

const MAX_SCALE = 8;
const WHEEL_STEP = 1.15;

type Props = {
  slices: SharedSliceRef[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
};

export default function SliceLightbox({ slices, index, onIndexChange, onClose }: Props) {
  const { t } = useTranslation('Medicaprof');

  const [showClean, setShowClean] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const stageRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef(1);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  // A pan that ends over empty space still raises a click, which would
  // otherwise read as "clicked the backdrop" and close the image.
  const panned = useRef(false);

  const slice = slices[index];
  const annotated = slice?.snapshotUrl || '';
  const clean = slice?.cleanSnapshotUrl || '';
  const label = slice?.label || t('Report.SliceLabel', { index: slice?.sliceIndex ?? 0 });

  const zoomTo = useCallback((next: number) => {
    const clamped = Math.min(Math.max(next, 1), MAX_SCALE);
    scaleRef.current = clamped;
    setScale(clamped);
    if (clamped === 1) {
      setOffset({ x: 0, y: 0 });
    }
  }, []);

  const step = useCallback(
    (delta: number) => {
      if (slices.length < 2) {
        return;
      }
      onIndexChange((index + delta + slices.length) % slices.length);
    },
    [index, slices.length, onIndexChange]
  );

  // Moving to another slice starts it the way it was saved, drawings and all.
  useEffect(() => {
    setShowClean(false);
    zoomTo(1);
  }, [index, zoomTo]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        step(1);
      } else if (e.key === 'ArrowLeft') {
        step(-1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step]);

  // The page behind must not scroll while the image is up.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // React's own wheel handler is passive, so preventDefault there would be
  // ignored and the page would scroll behind the zoom.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) {
      return undefined;
    }
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomTo(scaleRef.current * (e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP));
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [zoomTo]);

  const onPointerDown = (e: React.PointerEvent) => {
    panned.current = false;
    if (scaleRef.current <= 1) {
      return;
    }
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) {
      return;
    }
    panned.current = true;
    setOffset({
      x: drag.current.ox + (e.clientX - drag.current.x),
      y: drag.current.oy + (e.clientY - drag.current.y),
    });
  };

  const endDrag = () => {
    drag.current = null;
  };

  if (!slice) {
    return null;
  }

  const imageClass =
    'block max-h-[calc(100vh-9rem)] max-w-[calc(100vw-2rem)] select-none object-contain';

  const iconBtn =
    'grid h-9 w-9 place-items-center rounded-md border-0 bg-transparent text-white transition-colors hover:bg-white/[.12] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#52a3df]';

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-[rgba(3,4,6,.96)] text-white [font-family:Roboto,Inter,system-ui,sans-serif]"
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div className="flex h-14 flex-none items-center gap-2 pl-5 pr-3">
        {slices.length > 1 && (
          <span className="text-[14px] font-medium tabular-nums leading-5">
            {t('Shared.ImageCounter', { index: index + 1, total: slices.length })}
          </span>
        )}
        <span className="ml-2 min-w-0 truncate text-[14px] leading-5 text-[#c7cad0] rtl:ml-0 rtl:mr-2">
          {label}
        </span>
        <span className="flex-1" />

        <div
          role="toolbar"
          aria-label={t('Shared.ResetZoom')}
          className="flex items-center gap-0.5 rounded-lg bg-white/[.08] p-0.5"
        >
          <button
            type="button"
            onClick={() => zoomTo(scaleRef.current / WHEEL_STEP)}
            title={t('Shared.ZoomOut')}
            aria-label={t('Shared.ZoomOut')}
            className={iconBtn}
          >
            <ZoomOut size={18} />
          </button>
          <button
            type="button"
            onClick={() => zoomTo(1)}
            title={t('Shared.ResetZoom')}
            aria-label={t('Shared.ResetZoom')}
            className="h-9 min-w-[48px] rounded-md border-0 bg-transparent px-2 text-[13px] font-medium tabular-nums text-white transition-colors hover:bg-white/[.12]"
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            type="button"
            onClick={() => zoomTo(scaleRef.current * WHEEL_STEP)}
            title={t('Shared.ZoomIn')}
            aria-label={t('Shared.ZoomIn')}
            className={iconBtn}
          >
            <ZoomIn size={18} />
          </button>
        </div>

        {clean && (
          <button
            type="button"
            aria-pressed={!showClean}
            onClick={() => setShowClean(v => !v)}
            className={`flex h-10 items-center gap-2 rounded-lg border border-white/25 px-3 text-[13px] font-medium text-white transition-colors hover:bg-white/[.12] ${
              showClean ? 'bg-transparent' : 'bg-white/[.12]'
            }`}
          >
            {showClean ? <EyeOff size={18} /> : <Eye size={18} />}
            {showClean ? t('Shared.ShowAnnotations') : t('Shared.HideAnnotations')}
          </button>
        )}

        <button
          type="button"
          onClick={onClose}
          title={t('Shared.Close')}
          aria-label={t('Shared.Close')}
          className="grid h-10 w-10 place-items-center rounded-lg border-0 bg-transparent text-white transition-colors hover:bg-white/[.12]"
        >
          <X size={22} />
        </button>
      </div>

      <div
        ref={stageRef}
        className="relative min-h-0 flex-1 overflow-hidden"
        style={{ touchAction: 'none' }}
      >
        <div
          className="absolute inset-0 flex items-center justify-center p-4"
          onClick={e => {
            if (panned.current) {
              panned.current = false;
              return;
            }
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDoubleClick={() => zoomTo(scaleRef.current > 1 ? 1 : 2.5)}
          style={{ cursor: scale > 1 ? 'grab' : 'default' }}
        >
          <div
            className="grid"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transformOrigin: 'center center',
            }}
          >
            {clean && (
              <img
                src={clean}
                alt={label}
                draggable={false}
                className={imageClass}
                style={{ gridArea: '1 / 1' }}
              />
            )}
            <img
              src={annotated}
              alt={label}
              draggable={false}
              className={imageClass}
              style={{ gridArea: '1 / 1', opacity: clean && showClean ? 0 : 1 }}
            />
          </div>
        </div>

        {slices.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => step(-1)}
              title={t('Shared.Previous')}
              aria-label={t('Shared.Previous')}
              className="absolute left-5 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border-0 bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              title={t('Shared.Next')}
              aria-label={t('Shared.Next')}
              className="absolute right-5 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border-0 bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {clean && showClean && (
          <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
            <span className="rounded-full bg-white/10 px-3 py-1 text-[12px] font-medium text-[#c7cad0]">
              {t('Shared.AnnotationsHidden')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
